import type { StoreSettings } from '@avrahami/contracts';
import { Alert, Button, Card, PageHeader, TextField } from '@avrahami/ui';
import { useCan } from '../api/session';
import { FormStatus } from '../components/fields';
import { useSettingsEditor, WithSettings } from '../components/settings';
import { StaleNotice } from '../components/versioned';
import { he } from '../i18n/he';

/** The store's own measurement IDs (part of the settings resource). */
export function AnalyticsPage() {
  const canWrite = useCan('store.settings.write');
  return (
    <>
      <PageHeader title={he.analytics.title} />
      <WithSettings>
        {(settings) => <AnalyticsForm settings={settings} canWrite={canWrite} />}
      </WithSettings>
    </>
  );
}

function AnalyticsForm({ settings, canWrite }: { settings: StoreSettings; canWrite: boolean }) {
  const { draft, set, submit, errors, localInvalid, save, stale, reload } =
    useSettingsEditor(settings);
  return (
    <Card>
      <form onSubmit={submit} noValidate data-testid="analytics-form">
        <fieldset disabled={!canWrite} className="flex max-w-xl flex-col gap-4">
          <StaleNotice stale={stale} onReload={reload} />
          <p className="text-sm text-slate-600">{he.analytics.intro}</p>
          {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}
          <TextField
            label={he.analytics.ga4}
            name="ga4MeasurementId"
            dir="ltr"
            maxLength={20}
            hint={he.analytics.ga4Hint}
            value={draft.ga4MeasurementId}
            error={errors['analytics.ga4MeasurementId']}
            onChange={(event) => {
              set({ ga4MeasurementId: event.target.value });
            }}
          />
          <TextField
            label={he.analytics.pixel}
            name="metaPixelId"
            dir="ltr"
            inputMode="numeric"
            maxLength={20}
            hint={he.analytics.pixelHint}
            value={draft.metaPixelId}
            error={errors['analytics.metaPixelId']}
            onChange={(event) => {
              set({ metaPixelId: event.target.value });
            }}
          />
          <FormStatus
            error={save.error}
            localInvalid={localInvalid}
            saved={save.isSuccess}
            onReload={reload}
          />
          {canWrite ? (
            <div>
              <Button type="submit" busy={save.isPending}>
                {he.common.save}
              </Button>
            </div>
          ) : null}
        </fieldset>
      </form>
    </Card>
  );
}
